import {
    Box,
    VStack,
    Text,
    Button,
    Input,
    Icon,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    Divider,
} from '@chakra-ui/react';
import { TbCar, TbShieldCheck, TbClock, TbHourglass, TbFileInvoice, TbChevronLeft, TbSortAscending, TbSortDescending, TbCalendar, TbArrowsSort, TbClipboardList } from 'react-icons/tb';
import { IconType } from 'react-icons';
import { useState, useEffect, useRef } from 'react';
import { AdditionalFilters, OrderOptionsFilter } from '../hooks/useHomeFeed';
import { useGetMakes } from '../hooks/useGetMakes';
import { useGetOrganizationInsurances } from '../hooks/useGetOrganizationInsurances';
import DatePicker from './DatePicker';
import {
    SearchableCheckboxMenu,
    CheckboxMenuRow,
    CheckboxMenuSearch,
    MenuRow,
    MenuSectionLabel,
    MenuFooterActions,
    SelectionSummaryTooltip,
    checkboxMenuPopoverContentSx,
    checkboxMenuTriggerMetrics,
} from '../ui/primitives/CheckboxMenu';
import { AddFilterMenu, ClearFiltersButton, FilterBar, FilterChip } from '../ui';

export interface AdvancedFiltersProps {
    filters: AdditionalFilters;
    onFiltersChange: (filters: AdditionalFilters) => void;
    showMyQuotes?: boolean;
}

type FilterType = 'makes' | 'insurances' | 'lastUpdated' | 'daysActive' | 'last15Days' | 'orderOptions' | 'quotingOptions' | 'potentialTotal' | 'quotesOnly' | 'oemEndorsement' | 'dealerReferral';
type OpenState = 'addFilter' | FilterType | null;

/** Top-level rows in the add-filter menu (quoting booleans live under Quoting Options). */
const FILTER_CATEGORIES: { key: FilterType; label: string; icon: IconType }[] = [
    { key: 'makes', label: 'Makes', icon: TbCar },
    { key: 'insurances', label: 'Insurance', icon: TbShieldCheck },
    { key: 'lastUpdated', label: 'Last Updated', icon: TbClock },
    { key: 'daysActive', label: 'Days Active', icon: TbHourglass },
    { key: 'last15Days', label: 'Last 15 Days', icon: TbCalendar },
    { key: 'orderOptions', label: 'Order Options', icon: TbClipboardList },
    { key: 'quotingOptions', label: 'Quoting Options', icon: TbFileInvoice },
];

const ORDER_OPTION_ITEMS: { value: OrderOptionsFilter; label: string }[] = [
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'partially-ordered', label: 'Partially Ordered' },
    { value: 'all-ordered', label: 'Fully Ordered' },
    { value: 'supplier-changes', label: 'Supplier Changes' },
];

const QUOTING_SUB_FILTER_LABELS = ['potential total', 'quotes only', 'oem endorsement', 'dealer referral', 'onsite', 'pre-order', 'preorder', 'expedited', 'my quotes'];

const QUOTING_PANEL_KEYS: FilterType[] = ['quotingOptions', 'potentialTotal', 'quotesOnly', 'oemEndorsement', 'dealerReferral'];


const AdvancedFiltersComponent = ({ filters, onFiltersChange, showMyQuotes = false }: AdvancedFiltersProps) => {
    const { data: makesData } = useGetMakes();
    const { data: insurancesData } = useGetOrganizationInsurances();

    // UI state
    const [openState, setOpenState] = useState<OpenState>(null);
    const [addFilterView, setAddFilterView] = useState<FilterType | null>(null);
    const [tempFilters, setTempFilters] = useState<AdditionalFilters & { quotingOptions?: 'onsite' | 'pre-order' | 'expedited' | 'my-quotes' }>(filters as any);

    // Search states
    const [filterSearch, setFilterSearch] = useState('');

    const containerRef = useRef<HTMLDivElement>(null);

    // Close popover and reset
    const closePopover = () => {
        setOpenState(null);
        setAddFilterView(null);
        setFilterSearch('');
    };

    // Toggle popover
    const togglePopover = (target: OpenState) => {
        if (openState === target) {
            closePopover();
        } else {
            setTempFilters({ ...filters } as any);
            setFilterSearch('');
            if (target === 'addFilter') {
                setAddFilterView(null);
            }
            setOpenState(target);
        }
    };

    // Click outside handler
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!openState) return;
            const target = event.target as HTMLElement;
            if (containerRef.current?.contains(target)) return;
            // Don't close if clicking inside a popover content (portaled)
            if (target.closest('[data-filter-popover]')) return;
            closePopover();
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openState]);

    // --- Temp filter handlers ---
    const handleTempMakesChange = (values: (string | number)[]) => {
        setTempFilters(prev => ({ ...prev, makes: values.length > 0 ? values as string[] : undefined }));
    };

    const handleTempInsurancesChange = (values: (string | number)[]) => {
        setTempFilters(prev => ({ ...prev, insuranceIds: values.length > 0 ? values as string[] : undefined }));
    };

    const handleTempLastUpdatedSort = (value: 'asc' | 'desc' | 'range') => {
        setTempFilters(prev => {
            const newFilters = { ...prev };
            if (prev.lastUpdatedSort === value) {
                delete newFilters.lastUpdatedSort;
                delete newFilters.lastUpdatedRange;
            } else {
                newFilters.lastUpdatedSort = value;
                if (value !== 'range') delete newFilters.lastUpdatedRange;
                delete newFilters.daysActiveSort;
                delete newFilters.daysActiveRange;
            }
            return newFilters;
        });
    };

    const handleTempLastUpdatedRange = (start: Date, end: Date) => {
        setTempFilters(prev => ({ ...prev, lastUpdatedRange: { start, end } }));
    };

    const handleTempDaysActiveSort = (value: 'asc' | 'desc' | 'range') => {
        setTempFilters(prev => {
            const newFilters = { ...prev };
            if (prev.daysActiveSort === value) {
                delete newFilters.daysActiveSort;
                delete newFilters.daysActiveRange;
            } else {
                newFilters.daysActiveSort = value;
                if (value !== 'range') delete newFilters.daysActiveRange;
                delete newFilters.lastUpdatedSort;
                delete newFilters.lastUpdatedRange;
            }
            return newFilters;
        });
    };

    const handleTempDaysActiveRange = (min: number, max: number) => {
        setTempFilters(prev => ({ ...prev, daysActiveRange: { min, max } }));
    };

    const handleTempLast15Days = (value: boolean) => {
        setTempFilters(prev => ({ ...prev, last15Days: value }));
    };

    const handleTempOrderOptionsChange = (values: (string | number)[]) => {
        setTempFilters(prev => ({
            ...prev,
            orderOptions: values.length > 0 ? (values as OrderOptionsFilter[]) : undefined,
        }));
    };

    const handleTempQuotingOptions = (value: 'onsite' | 'pre-order' | 'expedited' | 'my-quotes') => {
        setTempFilters(prev => {
            const newFilters = { ...prev };
            if (prev.quotingOptions === value) {
                delete newFilters.quotingOptions;
            } else {
                (newFilters as any).quotingOptions = value;
            }
            return newFilters;
        });
    };

    const handleTempQuotingBoolean = (filterKey: 'potentialTotal' | 'quotesOnly' | 'oemEndorsement' | 'hasDealerReferral') => {
        setTempFilters(prev => {
            const newFilters = { ...prev };
            if (prev[filterKey] === true) {
                delete (newFilters as any)[filterKey];
            } else {
                (newFilters as any)[filterKey] = true;
            }
            return newFilters;
        });
    };

    const isQuotingFilterGroupActive = () =>
        !!(filters.quotingOptions || filters.potentialTotal || filters.quotesOnly || filters.oemEndorsement || filters.hasDealerReferral);

    // Apply
    const handleApply = () => {
        onFiltersChange(tempFilters as AdditionalFilters);
        closePopover();
    };

    // Remove specific filter
    const removeFilter = (filterType: FilterType) => {
        const newFilters = { ...filters };
        switch (filterType) {
            case 'makes': delete newFilters.makes; break;
            case 'insurances': delete newFilters.insuranceIds; break;
            case 'lastUpdated': delete newFilters.lastUpdatedSort; delete newFilters.lastUpdatedRange; break;
            case 'daysActive': delete newFilters.daysActiveSort; delete newFilters.daysActiveRange; break;
            case 'last15Days': delete newFilters.last15Days; break;
            case 'orderOptions': delete newFilters.orderOptions; break;
            case 'quotingOptions': delete newFilters.quotingOptions; break;
            case 'potentialTotal': delete newFilters.potentialTotal; break;
            case 'quotesOnly': delete newFilters.quotesOnly; break;
            case 'oemEndorsement': delete newFilters.oemEndorsement; break;
            case 'dealerReferral': delete newFilters.hasDealerReferral; break;
        }
        onFiltersChange(newFilters);
        if (openState === filterType) closePopover();
    };

    const clearAllFilters = () => {
        onFiltersChange({});
        closePopover();
    };

    // --- Computed values ---
    const isFilterActive = (filterType: FilterType): boolean => {
        switch (filterType) {
            case 'makes': return !!(filters.makes && filters.makes.length > 0);
            case 'insurances': return !!(filters.insuranceIds && filters.insuranceIds.length > 0);
            case 'lastUpdated': return !!filters.lastUpdatedSort;
            case 'daysActive': return !!filters.daysActiveSort;
            case 'last15Days': return filters.last15Days === false;
            case 'orderOptions': return !!(filters.orderOptions && filters.orderOptions.length > 0);
            case 'quotingOptions': return !!filters.quotingOptions; // radio only; menu row uses isQuotingFilterGroupActive
            case 'potentialTotal': return filters.potentialTotal === true;
            case 'quotesOnly': return filters.quotesOnly === true;
            case 'oemEndorsement': return filters.oemEndorsement === true;
            case 'dealerReferral': return filters.hasDealerReferral === true;
        }
    };

    const getFilterLabel = (filterType: FilterType): string => {
        switch (filterType) {
            case 'makes': {
                const makeNames = (filters.makes || [])
                    .map(id => makesData?.makes?.find(m => m._id === id)?.name)
                    .filter(Boolean);
                return makeNames.length === 1 ? `Makes: ${makeNames[0]}` : `Makes: ${makeNames.length} selected`;
            }
            case 'insurances': {
                const insuranceNames = (filters.insuranceIds || [])
                    .map(id => insurancesData?.insuranceCompanies?.find(i => i._id === id)?.name)
                    .filter(Boolean);
                return insuranceNames.length === 1 ? `Insurance: ${insuranceNames[0]}` : `Insurance: ${insuranceNames.length} selected`;
            }
            case 'lastUpdated': {
                const sortLabel = filters.lastUpdatedSort === 'asc' ? 'Ascending' :
                    filters.lastUpdatedSort === 'desc' ? 'Descending' : 'Date Range';
                return `Last Updated: ${sortLabel}`;
            }
            case 'daysActive': {
                const sortLabel = filters.daysActiveSort === 'asc' ? 'Ascending' :
                    filters.daysActiveSort === 'desc' ? 'Descending' : 'Range';
                return `Days Active: ${sortLabel}`;
            }
            case 'last15Days': {
                return filters.last15Days === false ? 'All time' : 'Last 15 days';
            }
            case 'orderOptions': {
                const opts = filters.orderOptions ?? [];
                if (opts.length === 0) return 'Order options';
                if (opts.length === 1) {
                    const lab = ORDER_OPTION_ITEMS.find(i => i.value === opts[0])?.label ?? opts[0];
                    return `Order options: ${lab}`;
                }
                return `Order options: ${opts.length} selected`;
            }
            case 'quotingOptions': {
                const optionLabel = filters.quotingOptions === 'onsite' ? 'Onsite' :
                    filters.quotingOptions === 'pre-order' ? 'Pre-Order' :
                    (filters.quotingOptions as string) === 'my-quotes' ? 'My Quotes' : 'Expedited';
                return `Quoting: ${optionLabel}`;
            }
            case 'potentialTotal': {
                return 'Potential Total';
            }
            case 'quotesOnly': {
                return 'Quotes Only';
            }
            case 'oemEndorsement': {
                return 'OEM Endorsement';
            }
            case 'dealerReferral': {
                return 'Dealer Referral';
            }
        }
    };

    /**
     * Names behind a collapsed "N selected" chip. Empty for filters whose chip
     * already spells out its value, which disables the tooltip.
     */
    const getFilterTooltipItems = (filterType: FilterType): string[] => {
        const names = (() => {
            switch (filterType) {
                case 'makes':
                    return (filters.makes || [])
                        .map(id => makesData?.makes?.find(m => m._id === id)?.name)
                        .filter(Boolean) as string[];
                case 'insurances':
                    return (filters.insuranceIds || [])
                        .map(id => insurancesData?.insuranceCompanies?.find(i => i._id === id)?.name)
                        .filter(Boolean) as string[];
                case 'orderOptions':
                    return (filters.orderOptions ?? [])
                        .map(opt => ORDER_OPTION_ITEMS.find(i => i.value === opt)?.label ?? opt);
                default:
                    return [];
            }
        })();
        return names.length > 1 ? names : [];
    };

    const activeFilterTags: { key: FilterType; label: string; icon: IconType }[] = [];
    FILTER_CATEGORIES.forEach(cat => {
        if (cat.key === 'quotingOptions') {
            if (filters.quotingOptions) {
                activeFilterTags.push({ key: cat.key, label: getFilterLabel('quotingOptions'), icon: cat.icon });
            }
        } else if (isFilterActive(cat.key)) {
            activeFilterTags.push({ key: cat.key, label: getFilterLabel(cat.key), icon: cat.icon });
        }
    });
    (['potentialTotal', 'quotesOnly', 'oemEndorsement', 'dealerReferral'] as const).forEach(key => {
        if (isFilterActive(key)) {
            activeFilterTags.push({ key, label: getFilterLabel(key), icon: TbFileInvoice });
        }
    });

    // --- Sub-components ---
    const FilterOption = ({ label, isSelected, onClick, icon }: { label: string; isSelected: boolean; onClick: () => void; icon?: IconType }) => (
        <MenuRow
            kind="single"
            isSelected={isSelected}
            onClick={onClick}
            icon={icon ? <Icon as={icon as React.ElementType} boxSize={4} /> : undefined}
        >
            {label}
        </MenuRow>
    );

    // Render filter-specific options
    const renderFilterOptions = (filterType: FilterType) => {
        switch (filterType) {
            case 'makes':
                return (
                    <SearchableCheckboxMenu
                        embed
                        showTriggerTokens={openState === 'addFilter'}
                        pinnedAllLabel="All OEMs"
                        emptyTriggerLabel="All OEMs"
                        options={(makesData?.makes || [])
                            .slice()
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map(m => ({ value: m._id, label: m.name }))}
                        value={tempFilters.makes || []}
                        onChange={(vals) => handleTempMakesChange(vals)}
                        searchPlaceholder="Search OEMs"
                    />
                );

            case 'insurances':
                return (
                    <SearchableCheckboxMenu
                        embed
                        showTriggerTokens={openState === 'addFilter'}
                        pinnedAllLabel="All Carriers"
                        emptyTriggerLabel="All Carriers"
                        options={(insurancesData?.insuranceCompanies || [])
                            .slice()
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map(i => ({ value: i._id, label: i.name }))}
                        value={tempFilters.insuranceIds || []}
                        onChange={(vals) => handleTempInsurancesChange(vals)}
                        searchPlaceholder="Search carriers"
                    />
                );

            case 'lastUpdated':
                return (
                    <VStack align="stretch" spacing={0} p="6px">
                        <FilterOption
                            label="Ascending"
                            icon={TbSortAscending}
                            isSelected={tempFilters.lastUpdatedSort === 'asc'}
                            onClick={() => handleTempLastUpdatedSort('asc')}
                        />
                        <FilterOption
                            label="Descending"
                            icon={TbSortDescending}
                            isSelected={tempFilters.lastUpdatedSort === 'desc'}
                            onClick={() => handleTempLastUpdatedSort('desc')}
                        />
                        <FilterOption
                            label="Date Range"
                            icon={TbCalendar}
                            isSelected={tempFilters.lastUpdatedSort === 'range'}
                            onClick={() => handleTempLastUpdatedSort('range')}
                        />
                        {tempFilters.lastUpdatedSort === 'range' && (
                            <Box px={3} py={2}>
                                <VStack spacing={2} align="stretch">
                                    <Box>
                                        <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>START DATE</Text>
                                        <DatePicker
                                            value={tempFilters.lastUpdatedRange?.start || new Date()}
                                            onChange={(date: Date) => {
                                                handleTempLastUpdatedRange(date, tempFilters.lastUpdatedRange?.end || new Date());
                                            }}
                                            size="sm"
                                        />
                                    </Box>
                                    <Box>
                                        <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>END DATE</Text>
                                        <DatePicker
                                            value={tempFilters.lastUpdatedRange?.end || new Date()}
                                            onChange={(date: Date) => {
                                                handleTempLastUpdatedRange(tempFilters.lastUpdatedRange?.start || new Date(), date);
                                            }}
                                            size="sm"
                                        />
                                    </Box>
                                </VStack>
                            </Box>
                        )}
                    </VStack>
                );

            case 'daysActive':
                return (
                    <VStack align="stretch" spacing={0} p="6px">
                        <FilterOption
                            label="Ascending"
                            icon={TbSortAscending}
                            isSelected={tempFilters.daysActiveSort === 'asc'}
                            onClick={() => handleTempDaysActiveSort('asc')}
                        />
                        <FilterOption
                            label="Descending"
                            icon={TbSortDescending}
                            isSelected={tempFilters.daysActiveSort === 'desc'}
                            onClick={() => handleTempDaysActiveSort('desc')}
                        />
                        <FilterOption
                            label="Range"
                            icon={TbArrowsSort}
                            isSelected={tempFilters.daysActiveSort === 'range'}
                            onClick={() => handleTempDaysActiveSort('range')}
                        />
                        {tempFilters.daysActiveSort === 'range' && (
                            <Box px={3} py={2}>
                                <VStack spacing={2} align="stretch">
                                    <Box>
                                        <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>MIN DAYS</Text>
                                        <Input
                                            type="number"
                                            size="sm"
                                            placeholder="0"
                                            value={tempFilters.daysActiveRange?.min ?? ''}
                                            onChange={(e) => {
                                                const min = parseInt(e.target.value) || 0;
                                                handleTempDaysActiveRange(min, tempFilters.daysActiveRange?.max || 999);
                                            }}
                                            bg="white"
                                            borderColor="gray.200"
                                            fontSize={checkboxMenuTriggerMetrics.fontSize}
                                        />
                                    </Box>
                                    <Box>
                                        <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>MAX DAYS</Text>
                                        <Input
                                            type="number"
                                            size="sm"
                                            placeholder="999"
                                            value={tempFilters.daysActiveRange?.max ?? ''}
                                            onChange={(e) => {
                                                const max = parseInt(e.target.value) || 999;
                                                handleTempDaysActiveRange(tempFilters.daysActiveRange?.min || 0, max);
                                            }}
                                            bg="white"
                                            borderColor="gray.200"
                                            fontSize={checkboxMenuTriggerMetrics.fontSize}
                                        />
                                    </Box>
                                </VStack>
                            </Box>
                        )}
                    </VStack>
                );

            case 'last15Days':
                return (
                    <VStack align="stretch" spacing={0} p="6px">
                        <FilterOption
                            label="Last 15 days"
                            icon={TbCalendar}
                            isSelected={tempFilters.last15Days !== false}
                            onClick={() => handleTempLast15Days(true)}
                        />
                        <FilterOption
                            label="All time"
                            icon={TbCalendar}
                            isSelected={tempFilters.last15Days === false}
                            onClick={() => handleTempLast15Days(false)}
                        />
                    </VStack>
                );

            case 'orderOptions':
                return (
                    <Box p="6px">
                        {ORDER_OPTION_ITEMS.map(({ value, label }) => {
                            const selected = (tempFilters.orderOptions ?? []).includes(value);
                            return (
                                <CheckboxMenuRow
                                    key={value}
                                    isSelected={selected}
                                    onClick={() => {
                                        const current = tempFilters.orderOptions ?? [];
                                        const next = selected
                                            ? current.filter(v => v !== value)
                                            : [...current, value];
                                        handleTempOrderOptionsChange(next);
                                    }}
                                >
                                    {label}
                                </CheckboxMenuRow>
                            );
                        })}
                    </Box>
                );

            case 'quotingOptions':
            case 'potentialTotal':
            case 'quotesOnly':
            case 'oemEndorsement':
            case 'dealerReferral':
                return (
                    <VStack align="stretch" spacing={0} p="6px">
                        <MenuSectionLabel>Quote type</MenuSectionLabel>
                        <FilterOption
                            label="Onsite"
                            isSelected={tempFilters.quotingOptions === 'onsite'}
                            onClick={() => handleTempQuotingOptions('onsite')}
                        />
                        <FilterOption
                            label="Pre-Order"
                            isSelected={tempFilters.quotingOptions === 'pre-order'}
                            onClick={() => handleTempQuotingOptions('pre-order')}
                        />
                        <FilterOption
                            label="Expedited"
                            isSelected={tempFilters.quotingOptions === 'expedited'}
                            onClick={() => handleTempQuotingOptions('expedited')}
                        />
                        {showMyQuotes && (
                            <FilterOption
                                label="My Quotes"
                                isSelected={(tempFilters.quotingOptions as string) === 'my-quotes'}
                                onClick={() => handleTempQuotingOptions('my-quotes')}
                            />
                        )}
                        <Divider borderColor="gray.200" my={2} />
                        <MenuSectionLabel>Order attributes</MenuSectionLabel>
                        <FilterOption
                            label="Potential Total"
                            isSelected={tempFilters.potentialTotal === true}
                            onClick={() => handleTempQuotingBoolean('potentialTotal')}
                        />
                        <FilterOption
                            label="Quotes Only"
                            isSelected={tempFilters.quotesOnly === true}
                            onClick={() => handleTempQuotingBoolean('quotesOnly')}
                        />
                        <FilterOption
                            label="OEM Endorsement"
                            isSelected={tempFilters.oemEndorsement === true}
                            onClick={() => handleTempQuotingBoolean('oemEndorsement')}
                        />
                        <FilterOption
                            label="Dealer Referral"
                            isSelected={tempFilters.hasDealerReferral === true}
                            onClick={() => handleTempQuotingBoolean('hasDealerReferral')}
                        />
                    </VStack>
                );

            default:
                return null;
        }
    };

    // Footer with Reset/Apply
    const renderFooter = (showReset: boolean = true) => (
        <MenuFooterActions
            secondaryLabel={showReset ? 'Reset' : 'Cancel'}
            onSecondary={showReset ? () => setTempFilters({}) : closePopover}
            onApply={handleApply}
        />
    );

    const handleCategoryClick = (filterKey: FilterType) => {
        setAddFilterView(filterKey);
    };

    /** Value preview on a drill-in row — the chip label without its category prefix. */
    const getCategorySummary = (filterKey: FilterType): string | undefined => {
        const isActive = filterKey === 'quotingOptions'
            ? isQuotingFilterGroupActive()
            : isFilterActive(filterKey);
        if (!isActive) return undefined;
        if (filterKey === 'quotingOptions' && !filters.quotingOptions) return 'On';
        const label = getFilterLabel(filterKey);
        const separator = label.indexOf(': ');
        return separator === -1 ? label : label.slice(separator + 2);
    };

    // Filtered category list for "Add filter" search
    const filteredCategories = FILTER_CATEGORIES.filter(cat => {
        if (!filterSearch) return true;
        const q = filterSearch.toLowerCase();
        if (cat.label.toLowerCase().includes(q)) return true;
        if (cat.key === 'quotingOptions') {
            return QUOTING_SUB_FILTER_LABELS.some(l => l.includes(q));
        }
        if (cat.key === 'orderOptions') {
            return (
                'order options'.includes(q) ||
                ORDER_OPTION_ITEMS.some(({ label }) => label.toLowerCase().includes(q))
            );
        }
        return false;
    });

    return (
        <Box ref={containerRef}>
        <FilterBar alignContent="center">
            {activeFilterTags.map(cat => (
                <Popover
                    key={cat.key}
                    isOpen={openState === cat.key}
                    placement="bottom-start"
                    gutter={8}
                    closeOnBlur={false}
                >
                    <PopoverTrigger>
                        <SelectionSummaryTooltip
                            items={getFilterTooltipItems(cat.key)}
                            isDisabled={openState === cat.key}
                            placement="bottom-start"
                        >
                            <FilterChip
                                icon={<Icon as={cat.icon as React.ElementType} boxSize={4} />}
                                label={cat.label}
                                onRemove={() => removeFilter(cat.key)}
                                onClick={() => togglePopover(cat.key)}
                            />
                        </SelectionSummaryTooltip>
                    </PopoverTrigger>
                    <PopoverContent w="300px" p={0} sx={checkboxMenuPopoverContentSx} data-filter-popover>
                        <PopoverBody p={0}>
                            {renderFilterOptions(QUOTING_PANEL_KEYS.includes(cat.key) ? 'quotingOptions' : cat.key)}
                            {renderFooter(false)}
                        </PopoverBody>
                    </PopoverContent>
                </Popover>
            ))}

            <AddFilterMenu
                isOpen={openState === 'addFilter'}
                onToggle={() => togglePopover('addFilter')}
                width={addFilterView ? "280px" : "220px"}
                contentProps={{ 'data-filter-popover': '' }}
            >
                        {addFilterView === null ? (
                            <>
                                <CheckboxMenuSearch
                                    value={filterSearch}
                                    onChange={setFilterSearch}
                                    placeholder="Search filters..."
                                />

                                {/* Category list */}
                                <VStack align="stretch" spacing={0} p="6px">
                                    {filteredCategories.map(cat => (
                                        <MenuRow
                                            key={cat.key}
                                            kind="drillIn"
                                            summary={getCategorySummary(cat.key)}
                                            icon={<Icon as={cat.icon as React.ElementType} boxSize={4} />}
                                            onClick={() => handleCategoryClick(cat.key)}
                                        >
                                            {cat.label}
                                        </MenuRow>
                                    ))}
                                </VStack>

                                {/* Reset / Apply */}
                                {renderFooter(true)}
                            </>
                        ) : (
                            <>
                                {/* Back button */}
                                <Box px={2} py={2} borderBottom="1px solid" borderColor="gray.100">
                                    <Button
                                        size="xs"
                                        variant="ghost"
                                        onClick={() => { setAddFilterView(null); }}
                                        leftIcon={<Icon as={TbChevronLeft as React.ElementType} />}
                                        color="gray.600"
                                        fontWeight="medium"
                                    >
                                        {(addFilterView && QUOTING_PANEL_KEYS.includes(addFilterView)
                                            ? FILTER_CATEGORIES.find(c => c.key === 'quotingOptions')
                                            : FILTER_CATEGORIES.find(c => c.key === addFilterView)
                                        )?.label}
                                    </Button>
                                </Box>

                                {/* Filter options inline */}
                                {renderFilterOptions(addFilterView)}

                                {/* Reset / Apply */}
                                {renderFooter(true)}
                            </>
                        )}
            </AddFilterMenu>

            {activeFilterTags.length > 0 && (
                <ClearFiltersButton onClick={clearAllFilters} />
            )}
        </FilterBar>
        </Box>
    );
};

export default AdvancedFiltersComponent;
